import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Save } from 'lucide-react';

interface DrSkuGlobalSettingsProps {
  organisationId: string;
  currentValue?: number;
  onUpdate?: (newValue: number) => void;
}

const DrSkuGlobalSettings: React.FC<DrSkuGlobalSettingsProps> = ({
  organisationId,
  currentValue = 0,
  onUpdate
}) => {
  const [drSkuPercentage, setDrSkuPercentage] = useState<string>(currentValue.toString());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setDrSkuPercentage(currentValue.toString());
  }, [currentValue]);

  const handleSave = async () => {
    const percentage = parseFloat(drSkuPercentage);
    
    // Validate percentage
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      toast({
        title: "Invalid percentage",
        description: "Percentage must be between 0 and 100",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('organisations')
        .update({ dr_sku_percentage: percentage })
        .eq('id', organisationId);

      if (error) {
        throw error;
      }

      toast({
        title: "Settings saved",
        description: `Global DR SKU percentage set to ${percentage}%`,
      });

      onUpdate?.(percentage);
    } catch (error) {
      console.error('Error updating DR SKU percentage:', error);
      toast({
        title: "Save failed",
        description: "Failed to update DR SKU percentage",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-lg">Global DR SKU Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="dr-sku-percentage">DR SKU Percentage (%)</Label>
          <Input
            id="dr-sku-percentage"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={drSkuPercentage}
            onChange={(e) => setDrSkuPercentage(e.target.value)}
            placeholder="0.00"
            disabled={saving}
          />
          <p className="text-sm text-muted-foreground">
            This percentage will be applied to all SKUs in the reports
          </p>
        </div>
        
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default DrSkuGlobalSettings;